import type { Discount } from '@nucleus-commerce/api-client/admin';
import { ApiError } from '@nucleus-commerce/api-client/transport';

/** Codes are case-insensitive on the backend; this is its accepted alphabet and length. */
export const CODE_PATTERN = /^[A-Za-z0-9_-]{3,40}$/;
export const PREFIX_PATTERN = /^[A-Za-z0-9_-]{0,12}$/;
export const MAX_GENERATE = 500;
/** What `appliesTo` holds at most (the API's limits). */
export const MAX_DISCOUNT_PRODUCTS = 500;
export const MAX_DISCOUNT_COLLECTIONS = 50;

export type ScheduleState = 'scheduled' | 'active' | 'expired' | 'disabled';

/** Disabled wins; otherwise the window decides. Limits reached are not a state of their own. */
export function scheduleState(
  discount: Pick<Discount, 'status' | 'startsAt' | 'endsAt'>,
  now: Date = new Date(),
): ScheduleState {
  if (discount.status === 'disabled') return 'disabled';
  if (discount.startsAt && new Date(discount.startsAt) > now) return 'scheduled';
  if (discount.endsAt && new Date(discount.endsAt) <= now) return 'expired';
  return 'active';
}

/**
 * The admin shows times in UTC today (the store context has no timezone yet), so a
 * datetime-local value is read and written as UTC wall time.
 */
export function toDateTimeInput(value: string | null): string {
  return value ? new Date(value).toISOString().slice(0, 16) : '';
}

export function fromDateTimeInput(value: string): string | null {
  return value === '' ? null : new Date(`${value}:00.000Z`).toISOString();
}

export function isCode(value: string): boolean {
  return CODE_PATTERN.test(value);
}

/** Case-insensitive dedupe that keeps the first spelling, as the backend does. */
export function uniqueCodes(codes: readonly string[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const code of codes) {
    const key = code.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(code);
  }
  return out;
}

export interface DiscountErrorTarget {
  field: 'codes' | 'root.server';
  key: string;
  values?: Record<string, unknown>;
}

/**
 * Where a save failure lands when the backend sent no usable `details`: a taken code on the
 * codes field, a limit on the form.
 */
export function discountErrorTarget(error: unknown): DiscountErrorTarget | null {
  if (!(error instanceof ApiError)) return null;
  if (error.code === 'ALREADY_EXISTS') return { field: 'codes', key: 'discounts:errors.codeTaken' };
  if (error.code === 'LIMIT_EXCEEDED') {
    return {
      field: 'root.server',
      key: 'discounts:errors.limit',
      values: { limit: error.meta.limit },
    };
  }
  return null;
}

/** `body.codes.3` and `body.amount.currency` belong to their parent field. */
export function discountFieldPath(field: string): string {
  return field
    .replace(/^codes\.\d+$/, 'codes')
    .replace(/\.(currency|amount)$/, '')
    .replace(/^appliesTo\.productIds.*/, 'productIds')
    .replace(/^appliesTo\.collectionIds.*/, 'collectionIds')
    .replace(/^conditions\.countries.*/, 'countries')
    .replace(/^conditions\./, '');
}

/**
 * Copy for a refused code deletion: a used code stays as its orders' record (switch it off
 * instead), and a code discount keeps its last code.
 */
export function codeDeleteErrorKey(error: unknown): string | null {
  if (!(error instanceof ApiError)) return null;
  if (error.code === 'RESOURCE_IN_USE') return 'discounts:codes.errors.inUse';
  if (error.code === 'CONFLICT') return 'discounts:codes.errors.lastCode';
  return null;
}

export const CODE_STATES = ['all', 'active', 'inactive'] as const;
export type CodeState = (typeof CODE_STATES)[number];

export function codeStateFilter(state: CodeState): boolean | undefined {
  return state === 'all' ? undefined : state === 'active';
}
